import _ from 'lodash';
import { Accessors } from 'vue/types/options';
import { ActionTree, GetterTree, MutationTree } from 'vuex';
import { ListStore, ListStoreBase, ListStoreProps, RowDetail } from '@/main/store/@types/list.d';
import { PageModule, RootStore } from '@/main/store/@types/store.d';
import { DropdownList } from '@/main/store/@types/dropdown.d';
import { listName } from '@/main/store/listEditTypes';

import { List } from './list';
import { getterAndSetterFromKeyDvm, getterFromKeyDvm, initStore, mapActionsDvm, mapMutationsDvm, MappedGetters } from './page';


/**
 * Generates `computed` properties for Vue components so that they can be accessed
 * directly as `this.propName`, not just indirectly with `this.$store.[ ... ].propName`.
 *
 * @param store - vuex store
 */
export function genVuexComputeds<
	StoreState extends ListStoreBase,
	Getters extends GetterTree<StoreState, RootStore>,
>(store: PageModule<StoreState, any, any, Getters>):
Accessors<StoreState & StoreState['filter'] & StoreState['localState'] & MappedGetters<Getters>> {
	const state = store.state();

	return {
		..._.mapValues(state, (keyValue, key: string, object:any) => getterAndSetterFromKeyDvm(key, object)),
		..._.mapValues(state.filter, (keyValue, key: string, object: any) => getterAndSetterFromKeyDvm(`filter.${key}`, object)),
		..._.mapValues(state.localState, (keyValue, key: string, object: any) => getterAndSetterFromKeyDvm(`localState.${key}`, object)),
		..._.mapValues(store.getters, (keyValue, key: string, object: any) => getterFromKeyDvm(key, object)),
	} as Accessors<StoreState & StoreState['filter'] & StoreState['localState'] & MappedGetters<Getters>>;
}

/**
 * Generates Vue component definition. A factory for List components.
 *
 * @param name - name of the component.
 * @param store - Vuex store of this component.
 */
export function listFactory<
	Filter extends object,
	TDropdownList extends DropdownList,
	TRowDetail extends RowDetail | null,
	LocalState extends object,
	Props extends ListStoreProps,
	Actions extends ActionTree<ListStore<Filter, TDropdownList, LocalState, TRowDetail, Props>, RootStore>,
	Mutations extends MutationTree<ListStore<Filter, TDropdownList, LocalState, TRowDetail, Props>>,
	Getters extends GetterTree<ListStore<Filter, TDropdownList, LocalState, TRowDetail, Props>, RootStore>,
>(name: string, store: PageModule<ListStore<Filter, TDropdownList, LocalState, TRowDetail, Props>, Actions, Mutations, Getters>) {
	return List.extend(
		{
			name: listName(name),
			computed: {
				...genVuexComputeds(store),
			},
			methods: {
				...mapActionsDvm(store.actions),
				...mapMutationsDvm(store.mutations),
			},
			beforeCreate() {
				initStore(this, listName(name), store);
			},
		},
	);
}
